import { IntegrationNotice } from '../IntegrationNotice'
import { useModals, Modal } from 'almanac-ui/Modal'
import Styles from '../IntegrationInitializationModal.module.scss'
import FormStyles from './ShopifyInitialization.module.scss'
import { Shopify } from 'blaze-ui/icons'
import { useState } from 'react'
import { Button } from 'blaze-ui/Button'
import { Field } from 'components/Wizards/Field/Field'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Text } from 'almanac-ui/Text/Text'
import { ArrowUpRightSquareContained } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { IntegrationHelpButton } from 'components/IntegrationHelpButton/IntegrationHelpButton'
import { isURL } from 'validator'
import { SwitchPlatformNotice } from 'components/TacticIntegrationList/SwitchPlatformNotice'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

export const ShopifyNotice = () => (
  <IntegrationNotice
    connectionGuideLink="https://help.blaze.ai/en/articles/connect-blaze-to-shopify"
    message={
      <div className={Styles.message}>
        To connect your Shopify store for blog publishing, ensure:
        <ul className={Styles.list}>
          <li>You have admin access to the Shopify store you want to connect.</li>
          <li>Your store has at least one blog created in the Shopify admin.</li>
        </ul>
        <div className={Styles.note}>
          <strong>Note:</strong> You'll be redirected to Shopify's secure login page to authorize Blaze's access to your
          store's blog.
        </div>
      </div>
    }
  />
)

const ModalTitle = ({ icon, title }: { icon: React.ReactNode; title: string }) => {
  return (
    <div className={Styles.modalTitle}>
      {icon}
      <Heading level={3}>{title}</Heading>
    </div>
  )
}

interface ShopifyInitializationProps {
  switchFromPlatform?: ExternalIntegrationEnum
  onConnect?: (shopDomain: string) => void
  switchFromIntegrationId?: number
}

export const ShopifyInitialization = ({
  onConnect,
  switchFromIntegrationId,
  switchFromPlatform,
}: ShopifyInitializationProps = {}) => {
  const { closeModal } = useModals()
  const [step, setStep] = useState<1 | 2 | 0>(switchFromIntegrationId && switchFromPlatform ? 0 : 1)
  const [shopDomain, setShopDomain] = useState('')
  const [error, setError] = useState<string | undefined>()

  const title = 'Connect Shopify Account'

  const normalizeShopDomain = (domain: string): string => {
    return domain
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '')
      .split('/')[0]
  }

  const validateShopDomain = (domain: string): string | undefined => {
    const normalized = normalizeShopDomain(domain)

    if (!normalized) {
      return 'Shop domain is required'
    }

    // Check if the normalized domain is a valid URL (with or without protocol)
    const isValidWithProtocol = isURL(normalized, { require_tld: true, require_protocol: true })
    const isValidWithHttps = isURL(`https://${normalized}`, { require_tld: true, require_protocol: true })

    if (!isValidWithProtocol && !isValidWithHttps) {
      return 'Please enter a valid shop domain (e.g., mystore.myshopify.com)'
    }

    return undefined
  }

  const handleConfirm = () => {
    const validationError = validateShopDomain(shopDomain)
    if (validationError) {
      setError(validationError)
      return
    }

    const normalized = normalizeShopDomain(shopDomain)
    setError(undefined)

    // Call onConnect with the shop domain instead of initiating OAuth directly
    onConnect?.(normalized)
  }

  if (switchFromPlatform && step === 0) {
    return (
      <SwitchPlatformNotice
        fromPlatform={switchFromPlatform}
        toPlatform={ExternalIntegrationEnum.Shopify}
        onSwitchPlatform={() => setStep(1)}
      />
    )
  }

  if (step === 1) {
    // Step 1: Instructions
    return (
      <Modal.Root size="sm" blaze onPressOutside={closeModal} aria-label="Connect to Shopify">
        <Modal.Header
          title={<ModalTitle icon={<Shopify size={32} />} title={title} />}
          actions={
            <div className={Styles.helpButtonContainer}>
              <IntegrationHelpButton size="sm" integration="Shopify" />
            </div>
          }
        />
        <Modal.Content>
          <ShopifyNotice />
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button variant="tertiary" onPress={closeModal}>
              Back
            </Button>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <Button onPress={() => setStep(2)}>Continue</Button>
          </Modal.FooterContent>
        </Modal.Footer>
      </Modal.Root>
    )
  }

  // Step 2: Form
  return (
    <Modal.Root size="sm" blaze onPressOutside={closeModal} aria-label="Connect to Shopify">
      <Modal.Header
        title={<ModalTitle icon={<Shopify size={32} />} title={title} />}
        actions={
          <div className={Styles.helpButtonContainer}>
            <IntegrationHelpButton size="sm" integration="Shopify" />
          </div>
        }
      />
      <Modal.Content>
        <div className={FormStyles.form}>
          <Text variant="p1" color="var(--neutral-50)">
            Enter your Shopify store URL to connect:
          </Text>

          {error && (
            <div className={FormStyles.errorContainer}>
              <span className={FormStyles.errorMessage}>{error}</span>
            </div>
          )}

          <Field label="Shopify Store URL" isRequired>
            <Input
              size="lg"
              name="shopDomain"
              value={shopDomain}
              errorMessage={error}
              placeholder="https://mystore.myshopify.com"
              onChange={(e) => {
                setShopDomain(e.target.value)
                setError(undefined)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleConfirm()
                }
              }}
            />
          </Field>

          <Text variant="p2" color="var(--neutral-50)">
            Enter the URL of your Shopify store.
          </Text>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" onPress={() => setStep(1)}>
            Back
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button
            onPress={handleConfirm}
            endIcon={ArrowUpRightSquareContained}
            aria-label="Go to Shopify"
            isDisabled={!shopDomain.trim()}
          >
            Go to Shopify
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
